import { useEffect, useRef, useState } from "react";
import {
  getAiScriptExplanationChatPath,
  isChatAccessDeniedError,
  useCreateAiScriptExplanationChat,
  useFollowAiScriptExplanationChatTurn,
  useGetAiScriptExplanationChatThread,
  useGetAiScriptExplanationChatThreads,
} from "@/payroll/ai-script/query";
import type {
  AiScriptChatSelectionContext,
  ExplanationChatPathBuilder,
} from "@/payroll/ai-script/types";
import {
  CHAT_VIEW,
  type ChatView,
  emptyThreadLiveState,
  mapPersistedMessage,
  THREAD_LIST_POLL_INTERVAL_MS,
} from "./chat-session-helpers";
import { hasSelectionContextContent } from "./selection-context";
import type { ChatMessage } from "./types";
import { useChatSend } from "./use-chat-send";
import { useFollowActiveTurn } from "./use-follow-active-turn";
import { useLiveReplyPlayback } from "./use-live-reply-playback";
import { usePendingQuestion } from "./use-pending-question";
import { useThreadLiveStates } from "./use-thread-live-states";

export const useAiScriptExplanationChatSession = ({
  businessId,
  runId,
  selectionContext,
  pendingQuestion,
  onQuestionPrepared,
  sendPendingQuestion = false,
  // Which chat this session talks to. Defaults to a payroll run's explanation
  // chat; profitability reports pass their own root and reuse everything else.
  buildChatPath = getAiScriptExplanationChatPath,
}: {
  businessId: string | null;
  runId: string;
  selectionContext?: AiScriptChatSelectionContext | null;
  pendingQuestion?: string | null;
  onQuestionPrepared?: () => void;
  /**
   * Sends `pendingQuestion` as soon as it arrives instead of leaving it in
   * the composer: a question asked from the page (Job Costing's "Ask Volca",
   * "Summarize") is already finished, while payroll's cell questions are a
   * start the user adds to.
   */
  sendPendingQuestion?: boolean;
  buildChatPath?: ExplanationChatPathBuilder;
}) => {
  const [view, setView] = useState<ChatView>(CHAT_VIEW.list);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const liveStates = useThreadLiveStates();
  const {
    liveStatesByThreadId,
    setLiveStatesByThreadId,
    localRunningTurnsByThreadId,
    setLocalRunningTurnsByThreadId,
  } = liveStates;
  const [isStartingNewTurn, setIsStartingNewTurn] = useState(false);
  const followedTurnKeysRef = useRef(new Set<string>());
  const selectedThreadIdRef = useRef<string | null>(null);
  // Tracks which thread's persisted history is loaded into local state, so
  // background refetches never clobber a richer in-session transcript.
  const loadedThreadIdRef = useRef<string | null>(null);
  // Jump straight into a new chat the first time the panel opens with no
  // existing threads.
  const hasAutoEnteredChatRef = useRef(false);
  // Whether the user is pinned to the bottom of the transcript; streamed
  // content only auto-follows while pinned, so scrolling up to read is never
  // snapped back down.
  const wasAtBottomRef = useRef(true);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { mutate: sendMessage } = useCreateAiScriptExplanationChat(
    businessId,
    buildChatPath,
  );
  const { mutate: followTurn } = useFollowAiScriptExplanationChatTurn(
    businessId,
    buildChatPath,
  );
  const {
    data: threadsData,
    error: threadsError,
    refetch: refetchThreads,
  } = useGetAiScriptExplanationChatThreads(businessId, runId, buildChatPath);
  const isAccessDenied = isChatAccessDeniedError(threadsError);
  const {
    data: threadData,
    isPending: isThreadPending,
    refetch: refetchThread,
  } = useGetAiScriptExplanationChatThread(
    businessId,
    runId,
    isAccessDenied ? null : selectedThreadId,
    buildChatPath,
  );

  const threads = isAccessDenied ? [] : (threadsData?.threads ?? []);
  const selectedThread = isAccessDenied
    ? null
    : (threads.find((thread) => thread.id === selectedThreadId) ?? null);
  const selectedThreadFromShow =
    !isAccessDenied && threadData?.thread.id === selectedThreadId
      ? threadData.thread
      : null;
  const selectedThreadLiveState = selectedThreadId
    ? liveStatesByThreadId[selectedThreadId]
    : undefined;
  const displayedLiveText = selectedThreadLiveState?.displayedText ?? "";
  const progressSteps = selectedThreadLiveState?.progressSteps ?? [];
  // Settle events can arrive in the same poll batch as the final text deltas,
  // flipping isBusy off while the typewriter is still behind targetText. The
  // catch-up animation and the live bubble must outlive isBusy, or the final
  // flush (gated on displayed === target) never runs and the reply vanishes.
  const hasUnplayedLiveText = selectedThreadLiveState
    ? selectedThreadLiveState.displayedText.length <
      selectedThreadLiveState.targetText.length
    : false;
  const hasRunningThread =
    Object.keys(localRunningTurnsByThreadId).length > 0 ||
    threads.some(
      (thread) =>
        thread.running ||
        !!thread.active_turn ||
        !!localRunningTurnsByThreadId[thread.id],
    );
  const isSelectedThreadRunning = selectedThreadId
    ? !!(
        selectedThreadLiveState?.isFollowing ||
        selectedThreadFromShow?.running ||
        selectedThreadFromShow?.active_turn ||
        selectedThread?.running ||
        selectedThread?.active_turn ||
        localRunningTurnsByThreadId[selectedThreadId]
      )
    : isStartingNewTurn;
  const isBusy =
    view === CHAT_VIEW.list ? isStartingNewTurn : isSelectedThreadRunning;
  // The query's own state, not "a thread is selected and the transcript is
  // empty". Inferring it from the message count meant a history fetch that
  // failed left a spinner running forever, because a failed query has no
  // messages either.
  const isThreadHistoryLoading =
    view === CHAT_VIEW.chat && !!selectedThreadId && isThreadPending;
  const attachedContext =
    selectionContext && hasSelectionContextContent(selectionContext)
      ? selectionContext
      : null;

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  useEffect(() => {
    loadedThreadIdRef.current = null;
    hasAutoEnteredChatRef.current = false;
    wasAtBottomRef.current = true;
    setView(CHAT_VIEW.list);
    setInput("");
    setMessages([]);
    setSelectedThreadId(null);
    setLiveStatesByThreadId({});
    setLocalRunningTurnsByThreadId({});
    setIsStartingNewTurn(false);
    followedTurnKeysRef.current.clear();
    // buildChatPath belongs here because the query keys are derived from the
    // path it builds, not from the ids.
  }, [businessId, runId, buildChatPath]);

  useEffect(() => {
    if (isAccessDenied || hasAutoEnteredChatRef.current || !threadsData) return;
    hasAutoEnteredChatRef.current = true;
    if (threadsData.threads.length === 0) setView(CHAT_VIEW.chat);
  }, [isAccessDenied, threadsData]);

  useEffect(() => {
    if (!hasRunningThread) return undefined;

    const interval = setInterval(() => {
      void refetchThreads();
    }, THREAD_LIST_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasRunningThread, refetchThreads]);

  // Seed the transcript from persisted history when entering a thread. The
  // in-session transcript stays authoritative afterwards (it carries steps and
  // selection-context chips that aren't persisted). `view` is a dependency
  // because reopening the same thread keeps the query key (and threadData
  // reference) unchanged, so the view transition is the only signal to reseed.
  useEffect(() => {
    if (view !== CHAT_VIEW.chat) return;
    if (!threadData || threadData.thread.id !== selectedThreadId) return;
    if (loadedThreadIdRef.current === threadData.thread.id) return;

    loadedThreadIdRef.current = threadData.thread.id;
    setMessages(threadData.messages.map(mapPersistedMessage));
    if (!threadData.thread.running && !threadData.thread.active_turn) {
      setLiveStatesByThreadId((currentStates) => ({
        ...currentStates,
        [threadData.thread.id]: emptyThreadLiveState(),
      }));
    }
  }, [threadData, selectedThreadId, view]);

  useFollowActiveTurn({
    businessId,
    runId,
    selectedThreadId,
    activeTurn: selectedThreadFromShow?.active_turn,
    followTurn,
    followedTurnKeysRef,
    selectedThreadIdRef,
    setMessages,
    liveStates,
  });

  useLiveReplyPlayback({
    isChatView: view === CHAT_VIEW.chat,
    bottomRef,
    wasAtBottomRef,
    messages,
    setMessages,
    isBusy,
    hasUnplayedLiveText,
    displayedLiveText,
    progressSteps,
    selectedThreadId,
    selectedThreadLiveState,
    liveStates,
  });

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const handleDraftChange = (value: string) => {
    setInput(value);
    resizeTextarea();
  };

  const resetComposer = () => {
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleOpenThread = (threadId: string) => {
    wasAtBottomRef.current = true;
    loadedThreadIdRef.current = null;
    setMessages([]);
    resetComposer();
    send.clearImages();
    // Same-thread reopen keeps the query key, so nothing refetches on its own;
    // other threads refetch automatically when their (stale) key remounts.
    if (threadId === selectedThreadIdRef.current) void refetchThread();
    setSelectedThreadId(threadId);
    setView(CHAT_VIEW.chat);
  };

  const handleNewChat = () => {
    wasAtBottomRef.current = true;
    setSelectedThreadId(null);
    loadedThreadIdRef.current = null;
    setMessages([]);
    resetComposer();
    send.clearImages();
    setView(CHAT_VIEW.chat);
  };

  const handleBackToList = () => {
    setView(CHAT_VIEW.list);
  };

  const send = useChatSend({
    runId,
    chatPath: buildChatPath(businessId ?? "", runId),
    input,
    isBusy,
    selectedThreadId,
    setSelectedThreadId,
    attachedContext,
    sendMessage,
    refetchThreads,
    setMessages,
    setView,
    setIsStartingNewTurn,
    resetComposer,
    wasAtBottomRef,
    followedTurnKeysRef,
    loadedThreadIdRef,
    selectedThreadIdRef,
    liveStates,
  });

  usePendingQuestion({
    question: pendingQuestion,
    send: sendPendingQuestion,
    onPrepared: onQuestionPrepared,
    setInput,
    textareaRef,
    conversation: {
      isBusy,
      isOpen: view === CHAT_VIEW.chat && !!selectedThreadId,
      handleSend: send.handleSend,
      handleStartNewChatWithMessage: send.handleStartNewChatWithMessage,
    },
  });

  const getIsThreadRunning = (threadId: string) => {
    const thread = threads.find(
      (candidateThread) => candidateThread.id === threadId,
    );
    const liveState = liveStatesByThreadId[threadId];

    return !!(
      liveState?.isFollowing ||
      thread?.running ||
      thread?.active_turn ||
      localRunningTurnsByThreadId[threadId]
    );
  };

  return {
    isAccessDenied,
    isListView: view === CHAT_VIEW.list,
    input,
    messages,
    threads,
    getIsThreadRunning,
    selectedThread,
    attachedContext,
    isBusy,
    isThreadHistoryLoading,
    displayedLiveText,
    progressSteps,
    bottomRef,
    textareaRef,
    handleDraftChange,
    handleOpenThread,
    handleNewChat,
    handleBackToList,
    // handleSend, handleStartNewChatWithMessage, and the composer's screenshots.
    ...send,
  };
};
